import "../Styles/CreateUser.css";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import UserProfileHeader from "./UserProfileHeader";

export default function AssignYubiKey() {
  const [sp] = useSearchParams();
  const token = sp.get("token");

  const API = useMemo(
    () => process.env.REACT_APP_API || process.env.REACT_APP_BACKEND_URL || "",
    []
  );

  const [msg, setMsg] = useState(null);
  const [serial, setSerial] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.post(`/get_message_by_token`, { token })
      .then(({data}) => {
        if (data?.success) setMsg(data.data);
        else toast.error(data?.msg || "Message not found");
      })
      .catch(() => toast.error("Load failed"))
      .finally(() => setLoading(false));
  }, [API, token]);

  const parseSerials = (raw) => {
    // split by comma, trim, remove empty, de-dup (keep order)
    const parts = String(raw || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const seen = new Set();
    const serials = [];
    for (const s of parts) {
      if (!seen.has(s)) {
        seen.add(s);
        serials.push(s);
      }
    }
    return serials;
  };

  const submit = () => {
    const qty = Number(msg?.order_keys_quantity || 1);
    const serials = parseSerials(serial);

    if (serials.length === 0) {
      return toast.error("Please enter a YubiKey serial.");
    }

    if (qty > 1) {
      if (serials.length !== qty) {
        return toast.error(
          `This order needs ${qty} serial numbers. You entered ${serials.length}. Please use commas to separate serials.`
        );
      }
    } else {
      // qty === 1
      if (serials.length !== 1) {
        return toast.error("This order needs 1 serial number only.");
      }
    }

    const operatorEmail = (localStorage.getItem("userEmail") || "").trim();
    if (!operatorEmail) return toast.error("Please log in first.");

    // Send as comma-joined string, backend will split & process
    axios
      .post(`/assign_yubikey`, {
        token,
        serial: serials.join(","), // IMPORTANT
        operator_email: operatorEmail,
      })
      .then(({ data }) => {
        if (data?.success) {
          toast.success("Serial assigned. Don’t forget to ship the YubiKey to the MDA.");
          // Optional: lock UI
          setMsg((m) => (m ? { ...m, status: "YubiKey Assigned" } : m));
        } else {
          toast.error(data?.msg || "Assign failed");
        }
      })
      .catch(() => toast.error("Network error"));
  };

  if (!token) return <div>Invalid link.</div>;
  if (loading) return <div>Loading…</div>;
  if (!msg) return <div>Message not found.</div>;

  const qty = Number(msg.order_keys_quantity || 1);
  const shipAddress = [msg.address, msg.city, msg.state, msg.country, msg.postal_code].filter(Boolean).join(", ") || "-";

  const inputDisabled = msg.status === "YubiKey Assigned" || msg.status === "VELOX CLOUD Key Assigned";
  const placeholder =
    qty > 1 ? `Enter ${qty} serials separated by commas (e.g., 123,456,789)` : "Enter serial";

  return (
    <div className="dashboard">
      <UserProfileHeader isAdmin={true} />
      <div className="container" style={{ padding: 16, maxWidth: 640 }}>
        <h2>Assign YubiKey Serial</h2>
        <p>
          <strong>Status:</strong> {msg.status}
        </p>

        <div style={{ marginTop: 12 }}>
          <h4>MDA Info</h4>
          <ul>
            <li>
              <strong>Name:</strong> {msg.first_name} {msg.last_name}
            </li>
            <li>
              <strong>Company:</strong> {msg.company_name}
            </li>
            <li>
              <strong>Email:</strong> {msg.email}
            </li>
            <li>
              <strong>Phone:</strong> {msg.phone || "-"}
            </li>
            <li>
              <strong>Ship Address:</strong> {shipAddress}
            </li>
            <li>
              <strong>Quantity of Keys:</strong> {qty}
            </li>
          </ul>
        </div>

        <div style={{ marginTop: 12 }}>
          <label htmlFor="yk-serial">
            <strong>VELOX CLOUD Key Serial</strong>
          </label>
          <input
            id="yk-serial"
            type="text"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder={placeholder}
            style={{ display: "block", width: "100%", padding: 8, marginTop: 6 }}
            disabled={inputDisabled}
          />

          {qty > 1 && (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
              Please enter exactly <strong>{qty}</strong> serial numbers, separated by commas.
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <button onClick={submit} disabled={inputDisabled}>
            Assign Serial
          </button>
        </div>

        <p style={{ marginTop: 16, color: "#aa0000" }}>
          <em>Reminder: Don’t forget to ship the YubiKey to the MDA after assigning the serial.</em>
        </p>
      </div>
    </div>
  );
}
